import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useState } from "react";

import {
	ROTATION_CONTROLS_HOVER_SX,
	SectionSpecRotationControls,
} from "@/pages/configuration/pages/sections/components/section-spec/SectionSpecRotationControls";
import { useSectionSpecFileRotation } from "@/pages/configuration/pages/sections/hooks/useSectionSpecFileRotation";
import { getRotatedAspectRatio } from "@/pages/configuration/pages/sections/models/SectionSpecRotation";
import { useObjectUrl } from "@/shared/hooks/useObjectUrl";
import type { DrawingPreview } from "../../hooks/queries/useSectionDrawingPreviewQuery";
import type { SectionSpec } from "@/shared/models/SectionSpec";
import type { SectionSpecFileKind } from "@/shared/models/SectionSpec";
import { formatMeasurement } from "@/shared/utils/measurementPrecision";

export const formatSpecValue = (value: number | null) =>
	value == null ? "-" : formatMeasurement(value);

export const formatWeightBand = (spec: SectionSpec) =>
	spec.MinLbsPerFoot !== null && spec.MaxLbsPerFoot !== null
		? `${formatSpecValue(spec.MinLbsPerFoot)}–${formatSpecValue(spec.MaxLbsPerFoot)}`
		: "no band loaded";

interface SectionSpecEmptyStateParams {
	sectionId: number | null;
	spec: SectionSpec | null;
	pending?: boolean;
	hasConfirmedRevisions?: boolean;
}

/** What the details panels show before a spec is available; null once it is. */
export const sectionSpecEmptyState = ({
	sectionId,
	spec,
	pending = false,
	hasConfirmedRevisions = false,
}: SectionSpecEmptyStateParams): ReactNode => {
	if (sectionId === null)
		return <Alert severity="info">Please select a section.</Alert>;
	if (pending && !spec) return <Box sx={{ minHeight: { xs: 260, lg: 440 } }} />;
	if (spec) return null;
	return (
		<Alert severity="info">
			{hasConfirmedRevisions
				? "No confirmed specification is effective at the selected sample time."
				: "This section has no published spec. Load its tolerances in Configuration > Sections before measuring."}
		</Alert>
	);
};

const DIALOG_TITLE_ID = "test-shack-sheet-drawing-dialog-title";

interface StatProps {
	label: string;
	value: string;
	hint?: string | null;
}

export const SectionDetailStat = ({ label, value, hint }: StatProps) => (
	<Box
		sx={{
			border: 1,
			borderColor: "divider",
			borderRadius: 1,
			minWidth: 0,
			px: 1.5,
			py: 0.75,
		}}
	>
		<Typography variant="caption" color="text.secondary" display="block">
			{label}
		</Typography>
		<Stack
			direction="row"
			alignItems="baseline"
			sx={{ minWidth: 0, flexWrap: "wrap", columnGap: 0.75 }}
		>
			<Typography
				sx={{
					minWidth: 0,
					fontSize: 17,
					fontWeight: 700,
					overflowWrap: "anywhere",
				}}
			>
				{value}
			</Typography>
			{hint && (
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{
						minWidth: 0,
						fontFamily: "monospace",
						overflowWrap: "anywhere",
					}}
				>
					{hint}
				</Typography>
			)}
		</Stack>
	</Box>
);

interface DrawingCardProps {
	spec: SectionSpec;
	kind: SectionSpecFileKind;
	fileName: string | null;
	title: string;
	icon: ReactNode;
	missingMessage: string;
	preview: DrawingPreview;
	onOpen: () => void;
}

export const SectionDrawingCard = ({
	spec,
	kind,
	fileName,
	title,
	icon,
	missingMessage,
	preview,
	onOpen,
}: DrawingCardProps) => {
	const [zoomed, setZoomed] = useState(false);
	const [failedUrl, setFailedUrl] = useState<string | null>(null);
	const rotation = useSectionSpecFileRotation({
		spec,
		kind,
		blob: preview.blob,
		fileName,
	});
	const rotatedUrl = useObjectUrl(rotation.change?.file ?? null);
	const objectUrl = rotatedUrl ?? preview.objectUrl;
	const aspectRatio = getRotatedAspectRatio(
		preview.aspectRatio,
		rotation.change,
	);
	const hasPreviewError =
		preview.isError || (objectUrl !== null && failedUrl === objectUrl);
	const isReady = objectUrl !== null && !hasPreviewError;
	const fitsPdfPage = preview.isPdf && aspectRatio !== null && !zoomed;

	return (
		<Box
			sx={{
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				display: "flex",
				flexDirection: "column",
				minHeight: { xs: 280, lg: 0 },
				minWidth: 0,
				overflow: "hidden",
			}}
		>
			<Stack
				direction="row"
				spacing={0.5}
				alignItems="center"
				sx={{
					borderBottom: 1,
					borderColor: "divider",
					flexShrink: 0,
					pl: 1.5,
					pr: 0.5,
					py: 0.5,
				}}
			>
				<Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
				<Typography
					variant="subtitle2"
					fontWeight={700}
					noWrap
					sx={{ flex: 1 }}
				>
					{title}
				</Typography>
				<IconButton
					size="small"
					disabled={!isReady || rotation.change !== null}
					title={`Open full size`}
					onClick={onOpen}
				>
					<OpenInFullIcon fontSize="small" />
				</IconButton>
			</Stack>

			<Box
				sx={{
					...ROTATION_CONTROLS_HOVER_SX,
					display: "flex",
					flex: 1,
					flexDirection: "column",
					minHeight: 0,
					position: "relative",
				}}
			>
				{isReady && (
					<SectionSpecRotationControls label={title} rotation={rotation} />
				)}
				<Box
					sx={{
						alignItems: zoomed ? "flex-start" : "center",
						containerType: fitsPdfPage ? "size" : "normal",
						display: "flex",
						flex: 1,
						justifyContent: zoomed ? "flex-start" : "center",
						minHeight: 0,
						overflow: zoomed ? "auto" : "hidden",
						p: 0.5,
					}}
				>
					{!preview.hasFile ? (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ m: "auto" }}
						>
							{missingMessage}
						</Typography>
					) : hasPreviewError ? (
						<Alert severity="error" role="alert" sx={{ m: "auto" }}>
							{title} could not be loaded.
						</Alert>
					) : objectUrl === null ? (
						<CircularProgress size={28} sx={{ m: "auto" }} />
					) : preview.isPdf ? (
						<Box
							component="iframe"
							src={`${objectUrl}${
								zoomed ? "#toolbar=0&zoom=175" : "#toolbar=0&view=Fit"
							}`}
							title={title}
							onError={() => setFailedUrl(objectUrl)}
							sx={
								fitsPdfPage
									? {
											aspectRatio,
											border: 0,
											height: "auto",
											width: `min(100%, calc(100cqh * ${aspectRatio}))`,
										}
									: { border: 0, height: "100%", width: "100%" }
							}
						/>
					) : (
						<Box
							component="img"
							src={objectUrl}
							alt={title}
							onError={() => setFailedUrl(objectUrl)}
							title={zoomed ? "Fit to the panel" : "Zoom"}
							onClick={() => setZoomed(!zoomed)}
							sx={{
								cursor: zoomed ? "zoom-out" : "zoom-in",
								maxHeight: zoomed ? "none" : "100%",
								maxWidth: zoomed ? "none" : "100%",
								objectFit: "contain",
								width: zoomed ? "220%" : "auto",
							}}
						/>
					)}
				</Box>
			</Box>
		</Box>
	);
};

interface DrawingDialogProps {
	title: string;
	label: string;
	preview: DrawingPreview;
	onClose: () => void;
}

export const SectionDrawingDialog = ({
	title,
	label,
	preview,
	onClose,
}: DrawingDialogProps) => (
	<Dialog
		open
		onClose={onClose}
		fullWidth
		maxWidth="xl"
		aria-labelledby={DIALOG_TITLE_ID}
	>
		<DialogTitle id={DIALOG_TITLE_ID} sx={{ pr: 6 }}>
			<Typography component="span" fontWeight={700}>
				{title}
			</Typography>
			<IconButton
				aria-label="Close the viewer"
				onClick={onClose}
				sx={{ position: "absolute", right: 12, top: 8 }}
			>
				<CloseIcon />
			</IconButton>
		</DialogTitle>
		<DialogContent dividers>
			<Box
				sx={{
					alignItems: "center",
					bgcolor: "background.default",
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					display: "flex",
					justifyContent: "center",
					minHeight: "70vh",
					overflow: "hidden",
					p: 1,
				}}
			>
				{preview.objectUrl === null ? (
					<CircularProgress aria-label={`Loading ${label.toLowerCase()}`} />
				) : preview.isPdf ? (
					<Box
						component="iframe"
						src={`${preview.objectUrl}#toolbar=0&view=Fit`}
						title={label}
						sx={
							preview.aspectRatio !== null
								? {
										aspectRatio: preview.aspectRatio,
										border: 0,
										width: `min(100%, calc(75vh * ${preview.aspectRatio}))`,
									}
								: { border: 0, height: "75vh", width: "100%" }
						}
					/>
				) : (
					<Box
						component="img"
						src={preview.objectUrl}
						alt={label}
						sx={{ maxHeight: "75vh", maxWidth: "100%", objectFit: "contain" }}
					/>
				)}
			</Box>
		</DialogContent>
	</Dialog>
);
